// hooks/usePost.ts — reactive hook for a single post by id.
//
// Reads from postStore reactively. Exposes engagement mutations.
// Components must NOT import postStore or postsService directly.

import { usePostStore } from '@/core/stores/postStore';
import { postsService } from '@/core/services/postsService';
import type { Post } from '@/core/models/post';

export interface PostHook {
  /** The post, or undefined when the id is not found. */
  post: Post | undefined;
  /** Toggle the liked state for this post. */
  /** Toggle the cheered state for this post. */
  toggleCheer: () => Promise<Post | undefined>;
  /** Spread (broadcast) this post to followers. */
  spread: () => Promise<Post | undefined>;
}

export function usePost(id: string): PostHook {
  // `id` is the raw URL param, and a post answers to THREE names: its legacy_id,
  // its current shareCode, and (1.24.00 D3) the `previousShareCode` it carried
  // before the `XX…` → `XG…` re-stamp. The retrieve endpoint resolves all three
  // and the store always keys the hydrated record under its legacy id, so all
  // three have to be matched here or the lookup reads "not found" for a post the
  // server already returned. `previousShareCode` is the whole point of the
  // re-stamp slice: an old share link must keep resolving forever.
  const post = usePostStore((s) =>
    s.posts.find(
      (p) => p.id === id || p.shareCode === id || p.previousShareCode === id,
    ),
  );
  // Detail-page ACTIONS (like/cheer/hide/poll-vote/...) accept legacy_id only — use the
  // RESOLVED post's id, never the raw URL param, which may be a shareCode.
  const resolvedId = post?.id ?? id;
  return {
    post,
    toggleCheer: () => postsService.toggleCheer(resolvedId),
    spread: () => postsService.spread(resolvedId),
  };
}
